import { isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { i18n } from '@shared/util/i18n';
import { buildCustomRRule } from './utils';

type MonthOption = {
  number: number;
  name: string;
};

export function YearOptionsDesktop({
  rruleOptions,
  setRruleOptions,
}: Pick<Batch, 'rruleOptions' | 'setRruleOptions'>) {
  // Must be read during render, not at module scope. Importing `moment/locale/*`
  // sets moment's global locale as a side effect, and those imports are evaluated
  // before the app syncs moment to the active language — so a module-scope
  // `moment.months()` would capture the last-imported locale's month names.
  const monthOptions = useMemo<MonthOption[]>(
    () => moment.months().map((name, index) => ({ number: index + 1, name })),
    [],
  );

  // Helper to ensure we always have a valid Date to reference
  const dtstartDate = useMemo(() => {
    if (rruleOptions?.dtstart) {
      return new Date(rruleOptions.dtstart);
    }
    return new Date();
  }, [rruleOptions]);

  const defaultMonth = dtstartDate.getUTCMonth() + 1; // RRule months are 1-indexed
  const defaultDay = dtstartDate.getUTCDate();

  /**
   * Selected months derived from rruleOptions – fallback to start month if none.
   */
  const selectedMonths = useMemo<number[]>(() => {
    if (!rruleOptions?.bymonth) {
      return [defaultMonth];
    }
    if (Array.isArray(rruleOptions.bymonth)) {
      return rruleOptions.bymonth as number[];
    }
    return [rruleOptions.bymonth as number];
  }, [rruleOptions, defaultMonth]);

  /**
   * Toggle a month in the selection.
   */
  const toggleMonth = useCallback(
    (month: MonthOption) => {
      const isSelected = selectedMonths.includes(month.number);
      let newSelection: number[];
      if (isSelected) {
        // Prevent clearing all months – must have at least one selected.
        if (selectedMonths.length === 1) {
          return;
        }
        newSelection = selectedMonths.filter(m => m !== month.number);
      } else {
        newSelection = [...selectedMonths, month.number];
      }
      // Sort for deterministic ordering (Jan=1 … Dec=12)
      newSelection.sort((a, b) => a - b);
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: newSelection,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    },
    [selectedMonths, defaultDay, setRruleOptions],
  );

  const monthsDisplay = useMemo(() => getMonthsDisplay(selectedMonths), [selectedMonths]);

  return (
    <View row centerVertically gap={1}>
      <Text size="body1" weight="semibold">
        {i18n('workflowTemplate.component.dateAndRepeat.onDotDotDot')}
      </Text>
      <Menu closeOnSelect={false}>
        <Menu.Button flex label={monthsDisplay} />
        <Menu.Content>
          <Menu.Options
            options={monthOptions}
            onSelect={toggleMonth}
            getItemKey={month => month.name}
            isItemSelected={item => selectedMonths.includes(item.number)}
            getItemLabel={item => item.name}
          />
        </Menu.Content>
      </Menu>
      <Text size="body1">{moment.localeData().ordinal(defaultDay)}</Text>
    </View>
  );
}

function getMonthsDisplay(months: number[]): string {
  const format = months.length > 1 ? 'MMM' : 'MMMM';
  const names = months.map(m =>
    moment()
      .month(m - 1)
      .format(format),
  );
  return names.join(', ');
}
